import { useUserDropdownV2Controller } from '@dealstack/frontend/shared/components/UserDropdownV2/useUserDropdownV2Controller';
import { TEST_ELEMENT_IDS } from '@dealstack/frontend/shared/helper/UIElementIdentifier/UIElementIdentifier';
import { useTranslationCommonUi } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { FEATURE_FLAG } from '@dealstack/shared/lib/common/featureFlag';
import { memo } from 'react';
import { MantineMenu } from '@dealstack/frontend/shared/components/MantineMenu/MantineMenu';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { Flex, Text } from '@mantine/core';
import { UserInitialsBadge } from '@dealstack/frontend/shared/components/UserInitialsBadge/UserInitialsBadge';
import { useDealstackFeatureFlag } from '@dealstack/frontend/shared/hooks/useDealstackFeatureFlag/useDealstackFeatureFlag.hook';
import { GENERIC_ELEMENT_ID } from '@dealstack/shared/tests/testElementIds';
import { useIsDealstackSupport } from '@dealstack/frontend/shared/hooks/useIsDealstackSupport';
import { FullscreenLoader } from '@dealstack/frontend/shared/components/loaders/FullLoaderScreenLoader/FullLoaderScreenLoader';

const UserDropdown = () => {
	const {
		onNavigateToUserProfile,
		onNavigateToHelpSection,
		onUserLogout,
		isSigningOut,
		isInvestorAppAndProfileNavbarEnabled,
		userName,
		userSource,
		isAdminApp,
		onNavigateToExportPage,
		onNavigateToOrganizationManagement,
		onNavigateToOrganizationsList,
		onNavigateToDocumentTemplates,
		hasDealstackSupportPermission,
	} = useUserDropdownV2Controller();

	const tCommonUi = useTranslationCommonUi();
	// ideally we shouldn't be using a feature flag inside a re usable component
	// the items in this component should possibly be props
	const showOrganizationManagement = useDealstackFeatureFlag(
		FEATURE_FLAG.ORGANIZATION_MANAGEMENT
	).value;

	// Must match the /templates route's gate exactly — flag AND support status — or the item
	// ships to admins the route redirects away and the API 403s. `useIsDealstackSupport` rather
	// than the controller's FGA permission, so item and route read the identical signal.
	const documentManagedTemplatesEnabled = useDealstackFeatureFlag(
		FEATURE_FLAG.DOCUMENT_MANAGED_TEMPLATES
	).value;
	const isDealstackSupport = useIsDealstackSupport();
	const canManageDocumentTemplates = documentManagedTemplatesEnabled && isDealstackSupport;

	return (
		<>
			{isSigningOut && <FullscreenLoader name="signingOut" />}
			<MantineMenu
				dataTestId={GENERIC_ELEMENT_ID.USER_DROPDOWN}
				position="bottom-end"
				targetContent={
					<Flex gap="0.25rem" align="center">
						<UserInitialsBadge
							firstName={userSource?.firstName}
							lastName={userSource?.lastName}
							textVariant="xs"
						></UserInitialsBadge>
						<Text variant="h6" fw={600} lineClamp={1} style={{ whiteSpace: 'nowrap' }}>
							{userName}
						</Text>
					</Flex>
				}
				items={[
					...(isInvestorAppAndProfileNavbarEnabled
						? [
								{
									key: 'user-profile',
									label: tCommonUi('action_buttons.user_profile'),
									onClick: onNavigateToUserProfile,
									dataTestId:
										TEST_ELEMENT_IDS.MAIN_LAYOUT_USER_DROPDOWN_USER_PROFILE,
								},
								{
									key: 'help',
									label: tCommonUi('action_buttons.help'),
									onClick: onNavigateToHelpSection,
									dataTestId: TEST_ELEMENT_IDS.MAIN_LAYOUT_USER_DROPDOWN_HELP,
								},
							]
						: []),
					...(isAdminApp
						? [
								{
									key: 'admin-user-profile',
									label: tCommonUi('action_buttons.user_profile'),
									onClick: onNavigateToUserProfile,
									dataTestId:
										TEST_ELEMENT_IDS.MAIN_LAYOUT_USER_DROPDOWN_USER_PROFILE,
								},
								{
									key: 'admin-exports',
									label: tCommonUi('action_buttons.exports'),
									onClick: onNavigateToExportPage,
								},
								...(showOrganizationManagement
									? [
											hasDealstackSupportPermission
												? {
														key: 'admin-organizations',
														label: tCommonUi(
															'action_buttons.organizations'
														),
														onClick: onNavigateToOrganizationsList,
														dataTestId:
															TEST_ELEMENT_IDS.MAIN_LAYOUT_USER_DROPDOWN_ORGANIZATION,
													}
												: {
														key: 'admin-organization',
														label: tCommonUi(
															'action_buttons.organization'
														),
														onClick: onNavigateToOrganizationManagement,
														dataTestId:
															TEST_ELEMENT_IDS.MAIN_LAYOUT_USER_DROPDOWN_ORGANIZATION,
													},
										]
									: []),
								...(canManageDocumentTemplates
									? [
											{
												key: 'admin-document-templates',
												label: tCommonUi(
													'action_buttons.document_templates',
													'Document templates'
												),
												onClick: onNavigateToDocumentTemplates,
												dataTestId:
													TEST_ELEMENT_IDS.MAIN_LAYOUT_USER_DROPDOWN_DOCUMENT_TEMPLATES,
											},
										]
									: []),
							]
						: []),
				]}
				bottomSectionItems={[
					{
						key: 'logout',
						label: tCommonUi('action_buttons.logout_v2'),
						onClick: onUserLogout,
						dataTestId: TEST_ELEMENT_IDS.MAIN_LAYOUT_USER_DROPDOWN_LOGOUT,
						danger: true,
						icon: <Icon variant="exit" size="lg" color="inherit" />,
					},
				]}
			/>
		</>
	);
};

const Memoized = memo(UserDropdown);

export { Memoized as UserDropdown };
